import { useEffect, useId, useMemo, useState } from "preact/hooks";
import { lobbySetMap, lobbySetMode, lobbySetRules } from "../../../game/lobby";
import type { LobbyView } from "../../../game/lobby";
import { mapName, modeName, requireMeta } from "../../../state/meta";
import { Button } from "../../components/Button";
import { Field, TextInput } from "../../components/Field";
import { OptionCard } from "../../components/OptionCard";
import { Slider } from "../../components/Slider";
import { Toggle } from "../../components/Toggle";
import { PLAYERS_MAX, PLAYERS_MIN, RULE_SPECS } from "../rooms/lobbySettings";
import { draftField, draftFromRules, draftToPatch, hasErrors, isDirty, validateDraft, visibleRules } from "./lobbyDraft";
import type { RulesDraft } from "./lobbyDraft";

/** The host's controls: mode, map and the rule editor (rules are applied together with the button). */
export function HostControls({ view }: { view: LobbyView }) {
  const catalog = requireMeta();
  const modeLabelId = useId();
  const mapLabelId = useId();
  const { rules } = view;
  const disabled = !view.canEdit;

  const [draft, setDraft] = useState<RulesDraft>(() => draftFromRules(rules));
  // The server's rules are the truth: a change from outside (applied rules, a new mode's defaults) replaces the draft.
  useEffect(() => setDraft(draftFromRules(rules)), [rules]);

  const errors = useMemo(() => validateDraft(rules.mode, draft, view.players.length), [rules.mode, draft, view.players.length]);
  const patch = useMemo(() => (hasErrors(errors) ? {} : draftToPatch(rules.mode, rules, draft)), [errors, rules, draft]);

  return (
    <div class="lobby-host">
      <section class="lobby-group" aria-labelledby={modeLabelId}>
        <h2 id={modeLabelId} class="lobby-group__title">
          Mode
        </h2>
        <div class="lobby-cards lobby-cards--modes" role="radiogroup" aria-labelledby={modeLabelId}>
          {catalog.modes.map((m) => (
            <OptionCard
              key={m.mode}
              role="radio"
              class="lobby-card"
              selected={m.mode === rules.mode}
              locked={disabled}
              onClick={() => lobbySetMode(m.mode)}
            >
              <span class="card__title">{m.name}</span>
            </OptionCard>
          ))}
        </div>
      </section>

      <section class="lobby-group" aria-labelledby={mapLabelId}>
        <h2 id={mapLabelId} class="lobby-group__title">
          Map
        </h2>
        <div class="lobby-cards lobby-cards--maps" role="radiogroup" aria-labelledby={mapLabelId}>
          {catalog.maps.map((m) => (
            <OptionCard
              key={m.id}
              role="radio"
              class="lobby-card"
              selected={m.id === rules.map}
              locked={disabled}
              title={m.description}
              onClick={() => lobbySetMap(m.id)}
            >
              <span class="card__title">{m.name}</span>
            </OptionCard>
          ))}
        </div>
        <p class="lobby-note">Everybody reloads the map when it changes.</p>
      </section>

      <section class="lobby-group lobby-group--rules" aria-label="Rules">
        <h2 class="lobby-group__title">Rules</h2>
        <Slider
          label="Max players"
          min={PLAYERS_MIN}
          max={PLAYERS_MAX}
          step={1}
          value={draft.maxPlayers}
          disabled={disabled}
          onChange={(value) => setDraft((d) => ({ ...d, maxPlayers: Math.round(value) }))}
        />
        {errors.maxPlayers !== undefined && <p class="lobby-error">{errors.maxPlayers}</p>}
        <div class="lobby-fields">
          {visibleRules(rules.mode).map((key) => {
            const field = draftField(key);
            return (
              <Field key={key} label={RULE_SPECS[key].label} error={errors[field]}>
                {(id) => (
                  <TextInput
                    id={id}
                    value={draft[field]}
                    onValue={(value) => setDraft((d) => ({ ...d, [field]: value }))}
                    disabled={disabled}
                    autoComplete="off"
                    invalid={errors[field] !== undefined}
                    onEnter={() => isDirty(patch) && lobbySetRules(patch)}
                  />
                )}
              </Field>
            );
          })}
        </div>
        <Toggle
          label="Friendly fire"
          checked={draft.friendlyFire}
          disabled={disabled}
          onChange={(friendlyFire) => setDraft((d) => ({ ...d, friendlyFire }))}
        />
        <div class="lobby-apply">
          <Button size="sm" disabled={disabled || !isDirty(patch)} onClick={() => lobbySetRules(patch)}>
            Apply rules
          </Button>
          {hasErrors(errors) && <span class="lobby-error">Fix the highlighted values first.</span>}
        </div>
      </section>
    </div>
  );
}

function limitText(value: number, suffix: string): string {
  return value > 0 ? `${value}${suffix}` : "-";
}

/** What everybody else sees of the rules: the same facts, read only. */
export function RulesSummary({ view }: { view: LobbyView }) {
  const { rules } = view;
  const rows: [string, string][] = [
    ["Mode", modeName(rules.mode)],
    ["Map", mapName(rules.map)],
    ["Max players", String(rules.maxPlayers)],
  ];
  for (const key of visibleRules(rules.mode)) {
    if (key === "timeLimitSec") rows.push([RULE_SPECS[key].label.replace(" (minutes)", ""), limitText(rules[key] / 60, " min")]);
    else rows.push([RULE_SPECS[key].label, limitText(rules[key], "")]);
  }
  rows.push(["Friendly fire", rules.friendlyFire ? "On" : "Off"]);
  return (
    <section class="lobby-group" aria-label="Match settings">
      <h2 class="lobby-group__title">Match settings</h2>
      <dl class="lobby-summary">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <p class="lobby-note">Only the host changes these. Pick your team on the left.</p>
    </section>
  );
}
